import React, { useEffect, useState } from 'react';
import { Table } from 'react-bootstrap';
import { FormattedMessage, FormattedNumber, injectIntl } from 'react-intl';
import { exportCompareSummaryReport } from '../../api/SummaryReport';

const DECIMALS = 3;

export interface ICompareFeeTableRow {
  description: string;
  kg: number;
  eur: number;
  eurKg: number;
  pomKg: number;
  pomKgRate: number;
  fee: number;
  feeEurKg: number;
  deviation: number;
}

interface ReportRow {
  [key: string]: any
}

export interface ICompareFeeTableProps {
  intl: any;
  header: string;
  data: ICompareFeeTableRow[];
  sumData: ICompareFeeTableRow[];
  clearingData: ICompareFeeTableRow[];
  compareHeader: string;
  compareData: ICompareFeeTableRow[];
  compareSumData: ICompareFeeTableRow[];
  compareClearingData: ICompareFeeTableRow[];
}

const COLUMNS = [
  'description',
  'kg',
  'eur',
  'eurKg',
  'pomKg',
  'pomKgRate',
  'fee',
  'feeEurKg',
  'deviation',
];

const CompareFeeTable: React.FunctionComponent<ICompareFeeTableProps> = (props: ICompareFeeTableProps) => {
  const [column, setColumn] = useState('');
  const [report, setReport] = useState([] as ICompareFeeTableRow[]);
  const [compare, setCompare] = useState([] as ICompareFeeTableRow[]);


  useEffect(() => {
    setReport(props.data);
  }, [props.data, props.header]);

  useEffect(() => {
    setCompare(props.compareData);
  }, [props.compareData, props.compareHeader]);


  const getTableRange = () => {
    const range: JSX.Element[] = [];
    range.push(<th className="empty" key={"report-compare-empty"}></th>);
    range.push(<th colSpan={8} className="report-compare range-header" key={"report-compare-range"}>{props.header}</th>);
    range.push(<th className="empty" key={"compare-report-empty"}></th>);
    range.push(<th colSpan={8} className="compare-report range-header" key={"compare-report-range"}>{props.compareHeader}</th>);
    return range;
  }

  const getTableHeaders = () => {
    const headers: JSX.Element[] = [];
    COLUMNS.forEach((name, index) => {
      const classes = ['btn btn-link header'];
      let className = "report-compare";
      if(name === 'description'){
        className= "";
        headers.push(
          <th key={name+index} className={className}>
                <FormattedMessage id={`summaryreport.datatable.${name}`}/>
          </th>
        );
      } else{
        headers.push(
          <th key={name+index} className={className}>
            <button className={classes.join(' ')} onClick={() => {setColumn(name);}}>
              <FormattedMessage id={`summaryreport.datatable.${name}`}/>
            </button>
          </th>
        );
      }

    })
    headers.push(
      <th key={"difference"}>
        <span className="header">
          <FormattedMessage id={'comparereport.datatable.difference'}/>
      </span></th>);
    COLUMNS.forEach((name) => {
      const classes = ['btn btn-link header'];
      if(name !== 'description')
        headers.push(
          <th key={name} className="compare-report">
            <button className={classes.join(' ')} onClick={() => {setColumn(name);}}>
              <FormattedMessage id={`summaryreport.datatable.${name}`}/>
            </button>
          </th>
        );
    })
    return headers;
  }

  const calculateDifference = (report: any, compare: any) => {
    let rep = report && report[column as keyof ICompareFeeTableRow] ? report[column as keyof ICompareFeeTableRow] : 0;
    let com = compare && compare[column as keyof ICompareFeeTableRow] ? compare[column as keyof ICompareFeeTableRow] : 0;
    let diff = rep-com;
    return diff;
  }

  const getDataTable = (report: any, compare: any) => {
    const options: { }[] = [];
    if ((report && Array.isArray(report)) ||
        (compare && Array.isArray(compare))) {
      for(let index = 0; index < report.length || index < compare.length; index++) {
        let description = report[index] ? report[index].description : compare[index] ? compare[index].description : "";
        let difference = calculateDifference(report[index], compare[index]);
        options.push(
          <tr key={index} className={description}>
            <td><FormattedMessage id={`summaryreport.browse.datatable.${description.toLowerCase()}`}/></td>
            {cell("kg", report[index])}
            {cell("eur", report[index])}
            {cell("eurKg", report[index], DECIMALS)}
            {cell("pomKg", report[index])}
            {cell("pomKgRate", report[index], 0)}
            {cell("fee", report[index])}
            {cell("feeEurKg", report[index], DECIMALS)}
            {cell("deviation", report[index], DECIMALS)}
            <td className={ "right" + ((Math.sign(difference) === 1) ? " positive" : ((Math.sign(difference) === -1) ? " negative" : "")) }>
              <FormattedNumber value={difference} maximumFractionDigits={column==='eurKg' ? DECIMALS : 0}/></td>
            {cell("kg", compare[index])}
            {cell("eur", compare[index])}
            {cell("eurKg", compare[index], DECIMALS)}
            {cell("pomKg", compare[index])}
            {cell("pomKgRate", compare[index], 0)}
            {cell("fee", compare[index])}
            {cell("feeEurKg", compare[index], DECIMALS)}
            {cell("deviation", compare[index], DECIMALS)}
          </tr>
        );
      }
    }
    return options;
  }

  const cell = (colname: string, row: ICompareFeeTableRow, decimals = 0) => {
    let value = row ? row[colname as keyof ICompareFeeTableRow] : 0;
    value = typeof(value) === 'number' ? value : parseFloat(value);
    return  <td className={ "right" + (column === colname ? " selected-column" : "")} >
              <FormattedNumber value={value} maximumFractionDigits={decimals} minimumFractionDigits={decimals}/>
            </td>;
  }

  const formatDataForExport = (data: ICompareFeeTableRow[]) => {
    let formatted = [] as ReportRow[];
    data.forEach((d: ICompareFeeTableRow) => {
      let row = {} as ReportRow;
      COLUMNS.forEach((name) => {
        let key = props.intl.formatMessage({id:`summaryreport.datatable.${name}`});
        let value = d[name as keyof typeof d];
        if(name === 'description')
          row[key] = props.intl.formatMessage({id:`summaryreport.browse.datatable.${value.toString().toLowerCase()}`});
        else row[key] = value;
      })
      formatted.push(row);
    });
    return formatted;
  }

  return (
    <div className="CompareReportTable">
      <button className="big-send-button btn btn-primary"
              onClick={() => exportCompareSummaryReport('compare',
                            {
                              header: props.header,
                              data: formatDataForExport(props.data),
                              compareHeader: props.compareHeader,
                              compareData: formatDataForExport(props.compareData),
                              summaryData: formatDataForExport(props.sumData),
                              summaryCompareData: formatDataForExport(props.compareSumData),
                              clearingData: formatDataForExport(props.clearingData),
                              clearingCompareData: formatDataForExport(props.compareClearingData)
                            }) }>
        <FormattedMessage id="comparereport.datatable.export"/>
      </button>
      <div className="table-responsive">
        <Table className="table table-sm">
          <thead className="reportDataHeader table">
            <tr>
              { getTableRange() }
            </tr>
            <tr className="table-headers table-bordered">
              { getTableHeaders() }
            </tr>
          </thead>
          <tbody className="reportData table table-bordered">
            { getDataTable(report, compare) }
          </tbody>
          <thead className="reportDataHeader table">
            <tr>
              <th colSpan={18} className="empty" key={"compare-report-empty"}></th>
            </tr>
          </thead>
          <tbody className="reportData table table-bordered">
            { getDataTable(props.sumData, props.compareSumData) }
          </tbody>
          <thead className="reportDataHeader table">
            <tr>
              <th colSpan={18} className="empty" key={"compare-report-empty"}></th>
            </tr>
          </thead>
          <tbody className="reportData table table-bordered">
            { getDataTable(props.clearingData, props.compareClearingData) }
          </tbody>
        </Table>
      </div>
    </div>
  )
}

export default injectIntl(CompareFeeTable);
